/**
 * ChallengeShowStrip
 *
 * The top of /challenges/welcome for someone who just registered: their show,
 * a live countdown, one-tap calendar and Zoom buttons, and one optional
 * question, "What trade do you want Rob to research live?".
 *
 * WHY THIS EXISTS (Sep 29 2026 show-up audit):
 * - Paid registrants almost never attended the free live show, and the show
 *   is where the Bootcamp sells. The welcome page led with a call booking and
 *   hid the show inside a collapsed panel.
 * - Rory Sutherland: people do not hate waiting, they hate not knowing how
 *   long. A countdown turns the days before the show into a visible wait.
 * - Writing down the trade you want researched is an active commitment
 *   (Cialdini). Our attendance numbers are too small to prove anything. The
 *   question left the form on Aug 23 to keep registration short, so it is asked
 *   here, after the seat is already saved, where it cannot cost a signup.
 *
 * Everything shown comes from the session row. Nothing is hardcoded: the day,
 * time and topic of the show change week to week.
 *
 * Full audit: MarketingManager/docs/investigations/2026-09-29_challenge-show-up-audit.md
 */
import React, { useEffect, useState } from 'react';
import { pushEvent } from '../../utils/analytics';
import {
  buildChallengeCalendarLinks,
  formatChallengeDate,
  formatLocalShowTime,
  formatShowCountdown,
  formatShowTimeEt,
} from './challengePageUtils';

type ChallengeSession = {
  id?: string;
  title?: string;
  scheduledAt?: string;
  durationMinutes?: number;
  duration?: string;
  joinUrl?: string;
};

type ChallengeShowStripProps = {
  session: ChallengeSession;
  apiUrl: string;
  readToken?: string | null;
  registrationId?: string | null;
};

type SaveState = 'idle' | 'saving' | 'saved' | 'failed';

// Minute-level text, so a check every 15 seconds is plenty and cheap.
const COUNTDOWN_REFRESH_MS = 15 * 1000;

const viewerTimeZone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

export default function ChallengeShowStrip({
  session,
  apiUrl,
  readToken,
  registrationId,
}: ChallengeShowStripProps) {
  const [now, setNow] = useState(() => Date.now());
  const [tradeIntent, setTradeIntent] = useState('');
  const [saveState, setSaveState] = useState<SaveState>('idle');

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), COUNTDOWN_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  const startMs = Date.parse(session.scheduledAt || '');
  const countdown = formatShowCountdown(startMs, now, session.durationMinutes);
  const links = buildChallengeCalendarLinks(session, apiUrl);
  const localTime = formatLocalShowTime(session.scheduledAt, viewerTimeZone());
  const isLive = countdown.state === 'live';

  const submitTradeIntent = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = tradeIntent.trim();
    if (!text || !readToken || saveState === 'saving') return;
    setSaveState('saving');
    try {
      const response = await fetch(`${apiUrl}/courses/challenge-registrations/trade-intent`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${readToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tradeIntent: text }),
      });
      if (!response.ok) throw new Error(`trade_intent_${response.status}`);
      setSaveState('saved');
      pushEvent({
        event: 'challenge_trade_intent_submitted',
        source: window.location.pathname,
        registration_id: registrationId || undefined,
      });
    } catch {
      setSaveState('failed');
    }
  };

  return (
    <section
      className="challenge-next__show"
      data-testid="challenge-show-strip"
      data-show-state={countdown.state}
      aria-labelledby="challenge-show-title"
    >
      <p className="challenge-next__show-eyebrow">Your live show</p>
      <h2 id="challenge-show-title" className="challenge-next__show-title">
        {session.title}
      </h2>
      <p className="challenge-next__show-when">
        <time dateTime={session.scheduledAt}>
          {formatChallengeDate(session.scheduledAt)} at {formatShowTimeEt(session.scheduledAt)}
        </time>
        {localTime ? (
          <span className="challenge-next__show-local">That is {localTime} your time.</span>
        ) : null}
      </p>
      {countdown.label ? (
        <p
          className="challenge-next__show-countdown"
          role="timer"
          aria-live="off"
          data-live={isLive}
        >
          {countdown.label}
        </p>
      ) : null}
      <div className="challenge-next__show-actions">
        {links && !isLive ? (
          <>
            <a
              className="challenge-next__action challenge-next__action--primary"
              href={links.google}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                pushEvent({
                  event: 'challenge_calendar_added',
                  calendar: 'google',
                  source: 'welcome_strip',
                })
              }
            >
              Add to Google Calendar
            </a>
            <a
              className="challenge-next__action"
              href={links.ics}
              download
              onClick={() =>
                pushEvent({
                  event: 'challenge_calendar_added',
                  calendar: 'ics',
                  source: 'welcome_strip',
                })
              }
            >
              Apple or Outlook calendar
            </a>
          </>
        ) : null}
        {session.joinUrl ? (
          <a
            className={`challenge-next__action${isLive ? ' challenge-next__action--primary' : ''}`}
            href={session.joinUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {isLive ? 'Join Now On Zoom' : 'Open Zoom Link'}
          </a>
        ) : null}
      </div>
      {readToken && !isLive ? (
        saveState === 'saved' ? (
          <p className="challenge-next__show-ask-done" role="status">
            Got it. Rob reads every one, and what people send usually decides where the night goes.
          </p>
        ) : (
          <form className="challenge-next__show-ask" onSubmit={submitTradeIntent}>
            <label htmlFor="challenge-trade-intent">
              What trade do you want Rob to research live?
            </label>
            <p className="challenge-next__show-ask-hint">
              Options, crypto, futures, a setup you keep checking. One line is plenty.
            </p>
            <div className="challenge-next__show-ask-row">
              <input
                id="challenge-trade-intent"
                type="text"
                maxLength={500}
                autoComplete="off"
                value={tradeIntent}
                onChange={(event) => setTradeIntent(event.target.value)}
              />
              <button
                type="submit"
                className="challenge-next__action challenge-next__action--primary"
                disabled={!tradeIntent.trim() || saveState === 'saving'}
              >
                Send It To Rob
              </button>
            </div>
            {saveState === 'failed' ? (
              <p className="challenge-next__show-ask-error" role="alert">
                That did not go through. Reply to your confirmation email instead, it reaches Rob
                too.
              </p>
            ) : null}
          </form>
        )
      ) : null}
    </section>
  );
}
